import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-5 w-16 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors font-sans">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-8 w-full items-center">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-6 w-32 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col items-center justify-center gap-8 p-8 w-full">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-2xl font-medium text-foreground hover:text-primary transition-colors font-serif" onClick={onClose}>
          {item.label}
        </Link>)}
      <Button variant="outline" className="mt-4 border-primary text-primary hover:bg-primary hover:text-primary-foreground w-full max-w-xs" asChild>
        <Link to="/ContactUsPage" onClick={onClose}>
          Discovery Call
        </Link>
      </Button>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);
  return <header id="header" className={cn("fixed top-0 left-0 right-0 z-50 transition-all duration-300 w-full", isScrolled ? "bg-background/80 backdrop-blur-md shadow-sm py-3 border-b border-transparent" : "bg-background border-b border-border py-5")}>
      <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
        {}
        <Link to="/" className="flex-shrink-0 z-50 relative">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1554409/img/logo-maia-lafortezza-HD.png" alt="Maia Lafortezza" className="h-20 w-auto object-contain" />
        </Link>

        {}
        <div className="hidden lg:flex items-center gap-8">
          <MenuProvider menu_id="37">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden lg:block">
          <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 rounded-sm px-6" asChild>
            <Link to="/ContactUsPage">Discovery Call</Link>
          </Button>
        </div>

        {}
        <Button variant="ghost" size="icon" className="lg:hidden z-50 relative hover:bg-transparent focus-visible:ring-0" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>

        {}
        <div className={cn("fixed inset-0 bg-background z-40 flex flex-col items-center justify-center transition-all duration-300 lg:hidden", isMobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none")}>
          <MenuProvider menu_id="37">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>
      </div>
    </header>;
}